// The one place the app talks to YouTube's player (SPEC F6, SPEC §10).
// - playsinline, rel 0, controls 1, enablejsapi 1, origin set.
// - loadVideoById runs inside the caller's tap handler; if the video doesn't
//   start, the state becomes 'blocked' and the UI says "Tap to play".
// - Errors 100, 101 and 150 go to onSkipError (dig the next one).
// - A 50 ms check enforces the loop (a preview loop, about +-100 ms).

import { useCallback, useEffect, useRef, useState } from 'react';
import { afterFirstPaint } from '../../lib/afterFirstPaint';
import { loadYouTubeApi, PlayerState, SKIP_ERRORS, type YtPlayer } from '../../lib/youtube';

export type PlayerStatus = 'loading' | 'idle' | 'playing' | 'paused' | 'blocked' | 'offline';

export interface LoopRange {
  on: boolean;
  inS: number | undefined;
  outS: number | undefined;
}

/** What the screen shows: plain values only */
export interface PlayerView {
  status: PlayerStatus;
  timeS: number;
  durationS: number;
  rates: number[];
  rate: number;
  /** The last error code YouTube reported for the current load (any code), or undefined */
  errorCode: number | undefined;
  /** The record that was loaded, or cued and then started from YouTube's own play button */
  startedId: string | undefined;
}

/** What the screen can do. Kept apart from PlayerView: these read the live player. */
export interface PlayerActions {
  /** Call inside a tap handler (iPhone needs the first play to come from a tap) */
  load: (videoId: string, startS?: number) => void;
  /** Shows a record in the player without playing it (no tap needed), so YouTube's own play button works on it */
  cue: (videoId: string, startS?: number) => void;
  togglePlay: () => void;
  seekTo: (s: number) => void;
  /** The player's exact time right now (view.timeS is refreshed 4 times a second) */
  getTime: () => number;
  setRate: (r: number) => void;
  retry: () => void;
}

export interface YouTubePlayerApi {
  /** Attach to the element the player renders into */
  containerRef: (el: HTMLDivElement | null) => void;
  view: PlayerView;
  act: PlayerActions;
}

const LOOP_CHECK_MS = 50;
const UI_TICK_MS = 250;
const BLOCKED_AFTER_MS = 1500;

export function useYouTubePlayer(opts: { loop: LoopRange; onSkipError: (code: number) => void }): YouTubePlayerApi {
  const [host, setHost] = useState<HTMLDivElement | null>(null);
  const player = useRef<YtPlayer | null>(null);
  const [status, setStatus] = useState<PlayerStatus>('loading');
  const [timeS, setTimeS] = useState(0);
  const [durationS, setDurationS] = useState(0);
  const [rates, setRates] = useState<number[]>([1]);
  const [rate, setRateState] = useState(1);
  const [attempt, setAttempt] = useState(0);
  const [errorCode, setErrorCode] = useState<number | undefined>();
  const [startedId, setStartedId] = useState<string | undefined>();
  const cued = useRef<string | undefined>(undefined);

  // Latest values for the timers and callbacks below, without re-creating the player
  const loop = useRef(opts.loop);
  const onSkip = useRef(opts.onSkipError);
  const statusRef = useRef<PlayerStatus>('loading');
  const blockedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Where to seek once the video it was asked for actually plays (see load)
  const pendingStart = useRef(0);
  useEffect(() => {
    loop.current = opts.loop;
    onSkip.current = opts.onSkipError;
  });
  const set = useCallback((s: PlayerStatus) => {
    statusRef.current = s;
    setStatus(s);
  }, []);

  useEffect(() => {
    if (!host) return;
    let cancelled = false;
    // The API replaces its target element, so give it a fresh child of the host
    cued.current = undefined; // a new player has cued nothing
    const target = document.createElement('div');
    host.replaceChildren(target);
    // The player box is there at once; the YouTube script starts after the first paint (v1.0 quality pass)
    const cancelLoad = afterFirstPaint(() => void loadYouTubeApi()
      .then((YT) => {
        if (cancelled) return;
        player.current = new YT.Player(target, {
          width: '100%',
          height: '100%',
          playerVars: { playsinline: 1, rel: 0, controls: 1, enablejsapi: 1, origin: window.location.origin },
          events: {
            onReady: (e) => {
              setRates(e.target.getAvailablePlaybackRates());
              set('idle');
            },
            onStateChange: (e) => {
              if (e.data === PlayerState.PLAYING) {
                clearTimeout(blockedTimer.current);
                if (cued.current) setStartedId(cued.current);
                if (pendingStart.current > 0) {
                  e.target.seekTo(pendingStart.current, true);
                  pendingStart.current = 0;
                }
                setDurationS(e.target.getDuration());
                setRates(e.target.getAvailablePlaybackRates());
                set('playing');
              } else if (e.data === PlayerState.PAUSED) set('paused');
              else if (e.data === PlayerState.ENDED) set('paused');
            },
            onError: (e) => {
              clearTimeout(blockedTimer.current);
              setErrorCode(e.data);
              if (SKIP_ERRORS.includes(e.data)) onSkip.current(e.data);
            },
          },
        });
      })
      .catch(() => {
        if (!cancelled) set('offline');
      }));
    return () => {
      cancelled = true;
      cancelLoad();
      clearTimeout(blockedTimer.current);
      try {
        player.current?.destroy();
      } catch {
        // Torn down before onReady: the real player has no destroy yet. Removing its iframe below is enough.
      }
      player.current = null;
      host.replaceChildren();
    };
  }, [host, attempt, set]);

  // The loop check (50 ms) and the on-screen clock (4 times a second). Chained
  // setTimeout, not setInterval: this polls the player, it schedules no notes.
  useEffect(() => {
    let alive = true;
    const every = (ms: number, fn: () => void) => {
      const run = () => {
        if (!alive) return;
        fn();
        setTimeout(run, ms);
      };
      setTimeout(run, ms);
    };
    every(LOOP_CHECK_MS, () => {
      const p = player.current;
      if (!p || statusRef.current !== 'playing') return;
      const { on, inS, outS } = loop.current;
      if (on && inS !== undefined && outS !== undefined && outS > inS && p.getCurrentTime() >= outS) p.seekTo(inS, true);
    });
    every(UI_TICK_MS, () => {
      const p = player.current;
      if (p && (statusRef.current === 'playing' || statusRef.current === 'paused')) setTimeS(p.getCurrentTime());
    });
    return () => {
      alive = false;
    };
  }, []);

  const load = useCallback((videoId: string, startS = 0) => {
    const p = player.current;
    // The real player only has its methods after onReady (status leaves 'loading')
    if (!p || statusRef.current === 'loading' || statusRef.current === 'offline') return;
    // No startSeconds: a cold player on a phone (a mark reopened from Marks, the first load after the page opens)
    // answered it with YouTube's generic playback error, where the same load on a warm player worked.
    // Load from the start and seek when it plays instead.
    pendingStart.current = startS;
    cued.current = videoId;
    setStartedId(videoId);
    setErrorCode(undefined);
    p.loadVideoById({ videoId });
    setTimeS(startS);
    clearTimeout(blockedTimer.current);
    // Autoplay can be refused: say so instead of sitting silent
    blockedTimer.current = setTimeout(() => {
      if (statusRef.current !== 'playing') set('blocked');
    }, BLOCKED_AFTER_MS);
  }, [set]);

  const cue = useCallback((videoId: string, startS = 0) => {
    const p = player.current;
    if (!p || statusRef.current === 'loading' || statusRef.current === 'offline' || cued.current === videoId) return;
    cued.current = videoId;
    pendingStart.current = 0;
    setErrorCode(undefined);
    p.cueVideoById({ videoId, startSeconds: startS });
  }, []);

  const togglePlay = useCallback(() => {
    const p = player.current;
    if (!p) return;
    if (statusRef.current === 'playing') p.pauseVideo();
    else p.playVideo();
  }, []);

  const getTime = useCallback(() => player.current?.getCurrentTime() ?? 0, []);

  const seekTo = useCallback((s: number) => {
    player.current?.seekTo(s, true);
    setTimeS(s);
  }, []);

  const setRate = useCallback((r: number) => {
    player.current?.setPlaybackRate(r);
    setRateState(r);
  }, []);

  const retry = useCallback(() => {
    set('loading');
    setStartedId(undefined);
    setAttempt((a) => a + 1);
  }, [set]);

  return {
    containerRef: setHost,
    view: { status, timeS, durationS, rates, rate, errorCode, startedId },
    act: { load, cue, togglePlay, seekTo, getTime, setRate, retry },
  };
}
